import React, {useState, useEffect, useRef} from "react";
import {Link, useLocation} from "react-router-dom";
import {Menu, X, ChevronDown, Download, User, Briefcase} from "lucide-react";
import {motion, AnimatePresence} from "framer-motion";
import {Button} from "./ui/button";
import Logo from "./Logo";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const downloadRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    {path: "/", label: "Home"},
    {path: "/services", label: "Services"},
    {path: "/how-it-works", label: "How It Works"},
    {path: "/become-worker", label: "Become a Worker"},
    {path: "/about", label: "About Us"},
    {path: "/contact", label: "Contact"},
  ];

  return (
    <motion.header
      initial={{y: -100}}
      animate={{y: 0}}
      transition={{duration: 0.5}}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <Logo className="h-8 w-auto sm:h-9 md:h-10" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-teal-600 ${
                  location.pathname === link.path
                    ? "text-teal-600"
                    : "text-gray-700"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons - Desktop */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-full"
              onClick={() => (window.location.href = "/services")}
            >
              Book Now
            </Button>

            {/* Download App Dropdown */}
            <div className="relative" ref={downloadRef}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setIsDownloadOpen((prev) => !prev);
                }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl border border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white transition-all duration-200 select-none cursor-pointer"
              >
                <Download size={16} />
                <span>Download App</span>
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${isDownloadOpen ? "rotate-180" : ""}`}
                />
              </a>

              <AnimatePresence>
                {isDownloadOpen && (
                  <motion.div
                    initial={{opacity: 0, y: -8, scale: 0.95}}
                    animate={{opacity: 1, y: 0, scale: 1}}
                    exit={{opacity: 0, y: -8, scale: 0.95}}
                    transition={{duration: 0.15}}
                    className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                  >
                    <a
                      href="/app-release-customer.apk"
                      download="maidly-customer.apk"
                      onClick={() => setIsDownloadOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-150"
                    >
                      <User size={16} className="text-teal-500" />
                      <div>
                        <div className="font-semibold">Customer App</div>
                      </div>
                    </a>
                    <div className="border-t border-gray-100" />
                    <a
                      href="/app-release-worker.apk"
                      download="maidly-worker.apk"
                      onClick={() => setIsDownloadOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-150"
                    >
                      <Briefcase size={16} className="text-teal-500" />
                      <div>
                        <div className="font-semibold">Worker App</div>
                      </div>
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{opacity: 0, height: 0}}
            animate={{opacity: 1, height: "auto"}}
            exit={{opacity: 0, height: 0}}
            transition={{duration: 0.3}}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <nav className="container mx-auto px-6 py-4 flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? "text-teal-600"
                      : "text-gray-700"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white w-full rounded-full"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  window.location.href = "/services";
                }}
              >
                Book Now
              </Button>

              {/* Mobile Download Options */}
              <div className="border-t border-gray-100 pt-2 space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
                  Download App
                </p>
                <a
                  href="/app-release-customer.apk"
                  download="maidly-customer.apk"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-teal-100 bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors duration-150"
                >
                  <User size={16} className="text-teal-500" />
                  <div>
                    <div className="text-sm font-semibold">Customer App</div>
                    <div className="text-xs text-teal-500">Book services easily</div>
                  </div>
                  <Download size={14} className="ml-auto text-teal-400" />
                </a>
                <a
                  href="/app-release-worker.apk"
                  download="maidly-worker.apk"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-teal-100 bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors duration-150"
                >
                  <Briefcase size={16} className="text-teal-500" />
                  <div>
                    <div className="text-sm font-semibold">Worker App</div>
                    <div className="text-xs text-teal-500">Manage your jobs</div>
                  </div>
                  <Download size={14} className="ml-auto text-teal-400" />
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
